import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import React from "react";
import { getCurrency } from "utilities";
import { additionalInformationSection } from "./components/additionalInformationSection";
import { dataProcessingDisclaimerSection } from "./components/dataProcessingDisclaimerSection";
import { defaultValuesSection } from "./components/defaultValuesSection";
import { financialOfferSection } from "./components/financialOfferSection";
import { formElementsSection } from "./components/formElementsSection";
import { mainSection } from "./components/mainSection";
import { WebformFormState } from "./components/model";
import { stylingSection } from "./components/stylingSection";
import { getEmbedCodeText } from "embedshowswizard/embedcodes";
import { afterSubmitSection } from "./components/afterSubmit";
import { removeEmptyTranslationValues } from "utilities/freefieldUtils";

interface WebformDetailSectionProps {
  webform: server.dto.Webform;
}

export const WebformDetailSection = (props: WebformDetailSectionProps) => {
  const [webformFormState, setWebformFormState] = React.useState<WebformFormState>({
    webformId: props.webform.webformId,

    titleInternal: props.webform.title || "",
    header: props.webform.header,
    description: props.webform.description,

    displayEventNote: props.webform.displayEventNote,
    eventNoteLabel: props.webform.eventNoteLabel,

    nameOfEvent: props.webform.eventName,
    timeOfPerformance: props.webform.eventTimeOfPerformance,
    eventWebsite: props.webform.eventWebsite,
    ticketPrice: props.webform.ticketPrice,

    financialOffer: props.webform.financialOffer,
    financialOfferDescription: props.webform.financialOfferDescription,
    availableCurrencies: props.webform.availableCurrencies.map(currency => getCurrency(currency)),

    defaultBookkeeping: props.webform.defaultBookkeeping,
    defaultStatus: props.webform.defaultStatus,
    defaultCountry: props.webform.defaultCountry,
    defaultUser: props.webform.defaultUser,
    defaultCulture: serverReference.interfaceCultures.find(c => c.lcid === props.webform.defaultCulture),
    availableCultures: serverReference.interfaceCultures.filter(c => props.webform.availableCultures.includes(c.lcid)),

    displayDataProcessingDisclaimer: props.webform.displayDataProcessingDisclaimer,
    dataProcessingDisclaimerText: props.webform.dataProcessingDisclaimerText,

    pageMode: props.webform.pageMode,
    customCss: props.webform.customCss,

    descriptionSuccess: props.webform.descriptionSuccess,
    recipientEmails: props.webform.recipientEmails.join(", "),
  });

  const onValueChanged = async (key: string, value: any) => {
    if (key === "titleInternal") {
      setWebformFormState(prevState => ({
        ...prevState,
        titleInternal: value
      }));
    } else if (key === "header") {
      setWebformFormState(prevState => ({
        ...prevState,
        header: value
      }));
    } else if (key === "description") {
      setWebformFormState(prevState => ({
        ...prevState,
        description: value
      }));
    } else if (key === "displayEventNote") {
      setWebformFormState(prevState => ({
        ...prevState,
        displayEventNote: value
      }));
    } else if (key === "eventNoteLabel") {
      setWebformFormState(prevState => ({
        ...prevState,
        eventNoteLabel: removeEmptyTranslationValues(value)
      }));
    } else if (key === "nameOfEvent") {
      setWebformFormState(prevState => ({
        ...prevState,
        nameOfEvent: value
      }));
    } else if (key === "timeOfPerformance") {
      setWebformFormState(prevState => ({
        ...prevState,
        timeOfPerformance: value
      }));
    } else if (key === "eventWebsite") {
      setWebformFormState(prevState => ({
        ...prevState,
        eventWebsite: value
      }));
    } else if (key === "ticketPrice") {
      setWebformFormState(prevState => ({
        ...prevState,
        ticketPrice: value
      }));
    } else if (key === "financialOffer") {
      setWebformFormState(prevState => ({
        ...prevState,
        financialOffer: value
      }));
    } else if (key === "financialOfferDescription") {
      setWebformFormState(prevState => ({
        ...prevState,
        financialOfferDescription: removeEmptyTranslationValues(value)
      }));
    } else if (key === "availableCurrencies") {
      setWebformFormState(prevState => ({
        ...prevState,
        availableCurrencies: value,
      }));
    } else if (key === "defaultBookkeeping") {
      setWebformFormState(prevState => ({
        ...prevState,
        defaultBookkeeping: value
      }));
    } else if (key === "defaultStatus") {
      setWebformFormState(prevState => ({
        ...prevState,
        defaultStatus: value
      }));
    } else if (key === "defaultCountry") {
      setWebformFormState(prevState => ({
        ...prevState,
        defaultCountry: value
      }));
    } else if (key === "defaultUser") {
      setWebformFormState(prevState => ({
        ...prevState,
        defaultUser: value
      }));
    } else if (key === "defaultCulture") {
      setWebformFormState(prevState => ({
        ...prevState,
        defaultCulture: value,
      }));
    } else if (key === "availableCultures") {
      setWebformFormState(prevState => ({
        ...prevState,
        availableCultures: value,
      }));
    } else if (key === "displayDataProcessingDisclaimer") {
      setWebformFormState(prevState => ({
        ...prevState,
        displayDataProcessingDisclaimer: value
      }));
    } else if (key === "dataProcessingDisclaimerText") {
      setWebformFormState(prevState => ({
        ...prevState,
        dataProcessingDisclaimerText: removeEmptyTranslationValues(value)
      }));
    } else if (key === "pageMode") {
      setWebformFormState(prevState => ({
        ...prevState,
        pageMode: value
      }));
    } else if (key === "customCss") {
      setWebformFormState(prevState => ({
        ...prevState,
        customCss: value
      }));
    } else if (key === "descriptionSuccess") {
      setWebformFormState(prevState => ({
        ...prevState,
        descriptionSuccess: removeEmptyTranslationValues(value)
      }));
    } else if (key === "recipientEmails") {
      setWebformFormState(prevState => ({
        ...prevState,
        recipientEmails: value
      }));
    }
  }

  const embedCode = getEmbedCodeText({ url: props.webform.formUrl, height: "1280", width: "600" });

  return <DetailSectionTemplate
    onValueChanged={onValueChanged}
    isInPageEditable
    sections={[
      mainSection(props.webform.formUrl, webformFormState),
      additionalInformationSection(webformFormState),
      formElementsSection(webformFormState),
      financialOfferSection(webformFormState),
      defaultValuesSection(webformFormState),
      dataProcessingDisclaimerSection(webformFormState),
      stylingSection(embedCode, webformFormState),
      afterSubmitSection(webformFormState),
    ]}
  />
}